import React from "react";
import HeroBgAnimation from "./HeroBgAnimation/HeroBgAnimation";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useSpring, animated } from "@react-spring/web";
import { FaCertificate, FaLocationArrow } from "react-icons/fa6";
import {
	HeroContainer,
	HeroBg,
	HeroLeftContainer,
	Img,
	HeroRightContainer,
	HeroInnerContainer,
	TextLoop,
	TextLoopPrefix,
	Title,
	Header2,
	Span,
	SubTitle,
	HeroActions,
	ResumeButton
} from "./HeroStyle";
import { SourishMukherjeeHeroImage } from "../../../assets";
import Typewriter from "typewriter-effect";
import { BIOGRAPHY } from "../../../constants/biography";
import { education, profile } from "../../../content";
import type { EducationEntry } from "../../../types/content";
import { KnowMoreButton, MagicButton } from "../../shared";

import "./heroSection.css";

/**
 * Served straight from `public/`, so this is an asset route rather than
 * professional content — it does not belong in the content layer.
 *
 * ----------------------------------------------------------------------------
 * `public/`, NOT `src/assets/`, AND THE DIFFERENCE IS THE WHOLE POINT
 * ----------------------------------------------------------------------------
 * Everything under `src/assets/` that is IMPORTED gets content-hashed by Vite
 * into `build/assets/` — `…-DmOIsaAS.jpeg`. That is exactly right for a picture
 * the page renders and exactly wrong for a document people link to, bookmark
 * and put in an email: the filename changes whenever the bytes do, so every URL
 * shared before the last edit is dead. `public/` is copied verbatim, so this
 * path is stable across every rebuild and every content change.
 *
 * (A PDF dropped in `src/assets/` and never imported is worse than either: it
 * is not hashed, not copied, and not served at all — it simply does not ship.
 * One was sitting there when this was written.)
 *
 * ----------------------------------------------------------------------------
 * THE 2-PAGER IS REAL NOW (D-082)
 * ----------------------------------------------------------------------------
 * This used to resolve to the 3-page master, byte-identical to
 * `/downloads/Sourish_Mukherjee_Master_Resume_2026.pdf`, because no genuine
 * 2-page edition existed. One does, and it was dropped at this filename exactly
 * as the previous note anticipated — **no code change, and every existing link
 * still resolves.** That is what the stable URL bought.
 *
 * The master stays at its own filename rather than being deleted. It is a
 * different document (3 pages, and it carries the workshop and NCC history this
 * one has no room for), it is already public, and deleting a published URL
 * breaks whoever has it.
 *
 * ----------------------------------------------------------------------------
 * AND THE STABLE URL WAS BROKEN ONCE, BY A RENAME, WITH EVERY GATE GREEN
 * ----------------------------------------------------------------------------
 * On 2026-09-06 the file was updated and renamed in the same commit, to
 * `Sourish_Mukherjee_Final_2_Pager_Resume_2026.pdf`. The constant below still
 * named the old path, so the most prominent button on the site answered 404 —
 * on `main` and on the remote. Nothing caught it, and every gate was right not
 * to: a file under `public/` is COPIED, not imported, so there is no module
 * specifier for `tsc` to resolve and no build step that can fail. (D-084 is the
 * same shape one step further out, where an absent BINARY still had an import
 * and still compiled, because the `*.jpeg` module declaration is a wildcard.)
 *
 * The fix was to put the new bytes back at the published path rather than to
 * repoint this constant, because those are not equivalent: repointing restores
 * the button and silently kills every `/downloads/Sourish_Mukherjee_Resume
 * _2026.pdf` link already shared, which is the exact cost the paragraph above
 * says the stable filename was chosen to avoid. The URL filename is also not
 * the one a recruiter ever sees — `RESUME_DOWNLOAD_FILENAME` below decides
 * what lands in their downloads folder.
 *
 * `prerender.mjs` now asserts that every `/downloads/…` href in the emitted
 * documents resolves to a file in `build/downloads/`, and refuses to report
 * clean when it finds no hrefs at all.
 *
 * VERIFIED AGAINST THE SITE, not assumed: read off the decompressed text, the
 * published 2-pager says **"8+ years" twice** and gives the Aeonix title as
 * **Product Manager**, which is what `experience.json` records as the departing
 * title. The master says Product Architect & Senior Software Engineer and
 * contains "Product Manager" zero times — which is why D-072 and D-077 both
 * carried an open item about it.
 */
/**
 * THE ALT TEXT FOR THE LARGEST IMAGE ON THE SITE, AND IT USED TO BE
 * `alt="hero-image"` — a string that describes nothing, passes every
 * "images have alt text" check, and tells a screen-reader user precisely
 * what an empty alt would have. It mattered less when the picture was a
 * plain portrait. The picture is now the IIM Kashipur convocation (D-083),
 * so the alt is a claim about the owner's education, which makes it CONTENT
 * (Article II.2) and bars writing the degree and the institution as literals
 * here. Both are read from the content layer, so the alt cannot drift from
 * `education.json` the way a hardcoded sentence would.
 *
 * Falls back to the name alone if that record is ever removed: a less
 * specific alt is a smaller failure than a sentence asserting a degree the
 * layer no longer records.
 */
const HERO_IMAGE_ALT = ((): string => {
	const iim = education.find((e: EducationEntry) => e.id === "iim-kashipur-epgdm");
	return iim ? `${profile.name} receiving the ${iim.degree} at ${iim.institution}` : `${profile.name}`;
})();

const RESUME_DOWNLOAD_PATH = "/downloads/Sourish_Mukherjee_Resume_2026.pdf";

/**
 * What the saved file is called on the visitor's disk.
 *
 * NAMED, RATHER THAN LEFT TO THE URL. A bare `download` attribute saves the
 * file under the last path segment, so this would land as
 * `Sourish_Mukherjee_Resume_2026.pdf` — fine today, and silently wrong the year
 * the file is renamed. It is also the one string here a recruiter actually
 * reads, in a downloads folder full of other people's resumes.
 */
const RESUME_DOWNLOAD_FILENAME = "Sourish_Mukherjee_Resume.pdf";

interface FadeProps {
	children: React.ReactElement;
	in?: boolean;
	onClick?: any;
	onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
	onExited?: (node: HTMLElement, isAppearing: boolean) => void;
	ownerState?: any;
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(props, ref) {
	const { children, in: open, onClick, onEnter, onExited, ownerState, ...other } = props;
	const style = useSpring({
		from: { opacity: 0 },
		to: { opacity: open ? 1 : 0 },
		onStart: () => {
			if (open && onEnter) {
				onEnter(null as any, true);
			}
		},
		onRest: () => {
			if (!open && onExited) {
				onExited(null as any, true);
			}
		}
	});

	return (
		<animated.div ref={ref} style={style} {...other}>
			{React.cloneElement(children, { onClick })}
		</animated.div>
	);
});

const style = {
	position: "absolute" as const,
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	// width: 400,
	bgcolor: "background.paper",
	border: "2px solid var(--border)",
	boxShadow: 24,
	p: 4
};

const HeroSection = () => {
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<div id="about">
			<HeroContainer>
				<HeroBg>
					<HeroBgAnimation />
				</HeroBg>
				<HeroInnerContainer>
					<HeroLeftContainer id="Left">
						<Header2>Hi, This is</Header2>
						<Title>
							{/* <br /> */}
							<div className="text-gradient">{BIOGRAPHY.name}</div>
						</Title>
						<TextLoop>
							<TextLoopPrefix>I am a</TextLoopPrefix>
							<Span className="gradient-text">
								<Typewriter
									options={{
										strings: BIOGRAPHY.roles,
										autoStart: true,
										loop: true
									}}
								/>
							</Span>
						</TextLoop>
						<SubTitle>{BIOGRAPHY.description}</SubTitle>
						{/* <div className="know-more-container" onClick={handleOpen}>
							...Know More
						</div> */}
						<Modal
							aria-labelledby="spring-modal-title"
							aria-describedby="spring-modal-description"
							open={open}
							onClose={handleClose}
							closeAfterTransition
							slots={{ backdrop: Backdrop }}
							slotProps={{
								backdrop: {
									TransitionComponent: Fade
								}
							}}
						>
							<Fade in={open}>
								<Box sx={style}>
									<div className="what-is-text"> {"About my Journey"}</div>
									<div className="main-para">
										With a profound passion for Full-Stack Development, Cloud Computing, and DevOps,
										I bring a diverse set of skills and experiences to the table. My career journey
										has been driven by a relentless pursuit of excellence, continuous learning, and
										a commitment to innovation.
									</div>
									<div className="second-text">
										As a Product Manager and Senior Software Engineer, I specialize in designing and
										implementing scalable, high-performance solutions that drive business growth and
										enhance user experiences. My expertise in the AWS ecosystem and the MERN stack
										enables me to build robust, end-to-end applications that meet the complex needs
										of modern enterprises.
									</div>
									<div className="second-para">
										Holding an Executive Post Graduate Diploma in Business Management from IIM
										Kashipur, I possess a strategic mindset that bridges the gap between technology
										and business. This unique combination allows me to advise on product
										development, streamline processes, and deliver impactful solutions that align
										with organizational goals.
									</div>
									<div className="second-para">
										I am dedicated to contributing to my organization by leveraging my technical
										skills, sharing knowledge, and collaborating with cross-functional teams. My
										academic background, including an MCA from MAKAUT and a PGDCA from IGNOU,
										provides a solid foundation in computer science and application development.
									</div>
									<div className="second-para">
										Beyond my professional endeavors, I am also a martial artist, which instills
										discipline, focus, and resilience—qualities that I carry into my work.
									</div>
									<div className="second-para">
										Lets connect and explore opportunities to innovate and achieve new heights
										together!
									</div>
								</Box>
							</Fade>
						</Modal>
						{/* One row, one set of alignment rules (D-080). These two had no
						    shared container: at 390 they were 42px out of line with an
						    83px width difference, and at 1280 they overlapped
						    vertically by 41px. */}
						<HeroActions>
							{/* `download` AND `target="_blank"` TOGETHER, deliberately.
							    For a same-origin file `download` wins and the browser
							    saves it; if it is ever served cross-origin (a CDN, a
							    storage bucket) the attribute is ignored per spec and
							    `target` is what stops the navigation replacing the
							    page. Keeping both means the button degrades to "opens
							    the PDF" rather than to "loses the site".
							    `rel="noopener noreferrer"` stays with the target. */}
							<ResumeButton
								href={RESUME_DOWNLOAD_PATH}
								download={RESUME_DOWNLOAD_FILENAME}
								target="_blank"
								rel="noopener noreferrer"
							>
								Download Resume
							</ResumeButton>
							<a href="#products" aria-label="Explore my projects">
								<MagicButton title="Explore my Projects" icon={<FaLocationArrow />} position="right" />
							</a>
						</HeroActions>

						{/* CREDLY CTA — dormant. If re-enabled, add
						    `import { socialUrl } from "../../../content";` and keep the href
						    derived rather than literal, so it cannot drift from profile.json
						    the way the old hardcoded slug did. */}
						{/* <a
							href={socialUrl("credly")}
							target="_blank"
							rel="noopener noreferrer"
							className="credly-cta"
						>
							<FaCertificate size={14} />
							View Verified Certifications on Credly
						</a> */}
					</HeroLeftContainer>

					<HeroRightContainer id="Right">
						<Img src={SourishMukherjeeHeroImage} alt={HERO_IMAGE_ALT} />
					</HeroRightContainer>
				</HeroInnerContainer>
			</HeroContainer>
		</div>
	);
};

export default HeroSection;
